import { createReducer, on } from '@ngrx/store';

import { goToProjects, setProject } from '../../core/store';
import { ExpositionEntity } from '../components/exposition-view/exposition.entity';
import {
	createExposition,
	createExpositionFail,
	createExpositionSuccess,
	deleteExpositionAccessSuccess,
	fetchAvailableConsumersSuccess,
	fetchAvailableExpositionsForMigrationSuccess,
	fetchAvailableExpositionsListSuccess,
	fetchAvailableTagsSuccess,
	fetchColumnConfigListSuccess,
	fetchDataBlockColumnsSuccess,
	fetchExpositionAccessSuccess,
	fetchExpositionColumnsSuccess,
	fetchExpositionDataBlockListSuccess,
	fetchExpositionEndpointMetadataSuccess,
	fetchExpositionHistorySuccess,
	fetchExpositionMetadataSuccess,
	fetchExpositionOnHistoryNavigationSuccess,
	fetchExpositionSuccess,
	fetchExpositionViewOnEndpointDetailsTrigger,
	fetchExpositionViewOnEndpointDetailsTriggerSuccess,
	fetchJobErrorMessageFail,
	fetchJobErrorMessageSuccess,
	fetchMigratedExpositionsListSuccess,
	getColumnConfigList,
	getDataBlocksColumns,
	getJobErrorMessage,
	initExpositionsList,
	initExpositionView,
	triggerPublishActionOnExposition,
	migrateSelectedExpositions,
	migrateSelectedExpositionsFailed,
	migrateSelectedExpositionsSuccess,
	projectImportConsumersToMapSuccess,
	publicationActionSuccess,
	triggerMultipleExpositionPublishActionsOnList,
	refreshAfterError,
	refreshDatablocksSuccess,
	refreshExpositionAfterWS,
	refreshExpositionView,
	retrievesConsumersToMapSuccess,
	selectExposition,
	updateExpositionData,
	updateExpositionDataOnList,
	updateExpositionParams,
	updateExpositionRunningStatus,
	fetchEligibleConsumersSuccess,
	resetEligibleConsumers,
	autoMappingSuccess,
	closeMigrateExpositionModal,
	refreshAfterErrorSuccess,
} from './actions';
import { ExpositionOriginalSliceState } from './expositions.state';

export const expositionOriginalReducer =
	createReducer<ExpositionOriginalSliceState>(
		new ExpositionOriginalSliceState(),
		on(goToProjects, setProject, (state) => state.clear()),
		on(selectExposition, (state) =>
			state.setInViewExposition(ExpositionEntity.build())
		),
		on(initExpositionsList, (state) => state.toggleLoading(true)),
		on(fetchAvailableExpositionsListSuccess, (state, action) =>
			state
				.toggleLoading(false)
				.setExpositionList(action.list)
				.setCanCreateExposition(action.canCreate)
		),
		on(fetchExpositionDataBlockListSuccess, (state, action) =>
			state
				.setDatablockList(action.datablocks)
				.setExpositionList(action.availableExpositions)
		),
		on(getDataBlocksColumns, (state) =>
			state.toggleLoadingDatablockColumns(true)
		),
		on(fetchDataBlockColumnsSuccess, (state, action) =>
			state
				.toggleLoadingDatablockColumns(false)
				.updateDatablockListWithColumns(action.datablockId, action.columns)
		),
		on(getColumnConfigList, (state) => state.toggleLoading(true)),
		on(fetchColumnConfigListSuccess, (state, action) =>
			state.setColumnConfigList(action.list).toggleLoading(false)
		),
		on(fetchAvailableTagsSuccess, (state, action) =>
			state.setTags(action.tags)
		),
		on(fetchAvailableConsumersSuccess, (state, action) =>
			state.setAvailableConsumers(action.consumers)
		),
		on(createExposition, migrateSelectedExpositions, (state) =>
			state.toggleIsSavingExposition(true)
		),
		on(createExpositionSuccess, (state) => state.resetStepper()),
		on(
			createExpositionFail,
			migrateSelectedExpositionsSuccess,
			migrateSelectedExpositionsFailed,
			(state) => state.toggleIsSavingExposition(false).setRequestId('')
		),
		on(fetchExpositionSuccess, (state, action) =>
			state
				.setInViewExposition(action.exposition)
				.setExpositionList(action.availableExpositions)
				.setExpositionViewPermissions(action.permissions)
		),
		on(fetchExpositionColumnsSuccess, (state, action) =>
			state
				.setInViewExposition(state.inViewExposition.setColumns(action.columns))
				.setExpositionViewPermissions(action.permissions)
		),
		on(fetchExpositionMetadataSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setMetadata(action.metadata)
			)
		),
		on(fetchExpositionEndpointMetadataSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setEndpointMetadata(action.metadata)
			)
		),
		on(fetchExpositionAccessSuccess, (state, action) => {
			const { exposition } = action;
			const updated = exposition.setAccessPoints(
				exposition.accessPoints.setCurrentConfig(action.access)
			);
			return state
				.setInViewExposition(updated)
				.setExpositionViewPermissions(action.permissions);
		}),
		on(deleteExpositionAccessSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setAccessPoints(
					state.inViewExposition.accessPoints.deleteAccess(action.accessId)
				)
			)
		),
		on(updateExpositionRunningStatus, (state, action) =>
			state.setExpositionList(
				state.expositions.setRunningActionOnItem(
					action.expositionId,
					'in-progress'
				)
			)
		),
		on(triggerPublishActionOnExposition, (state) =>
			state.setActionInProgress('publish')
		),
		// on(triggerMultipleExpositionPublishActionsOnList, (state, action) =>
		// 	state.setExpositionList(
		// 		state.expositions.setRunningActionOnItem(action.expositionId, 'publish')
		// 	)
		// ),
		on(updateExpositionParams, (state) =>
			state
				.setActionInProgress('update')
				.setInViewExposition(
					state.inViewExposition.setPublishedApi(
						state.inViewExposition.published.addInProgressRowToUpdates()
					)
				)
		),
		on(updateExpositionData, (state) =>
			state
				.setActionInProgress('update-data')
				.setInViewExposition(
					state.inViewExposition.setPublishedApi(
						state.inViewExposition.published.addInProgressRowToUpdates()
					)
				)
		),
		on(updateExpositionDataOnList, (state, action) =>
			state.setExpositionList(
				state.expositions.setRunningActionOnItem(
					action.expositionId,
					'update-data'
				)
			)
		),
		on(
			initExpositionView,
			refreshExpositionView,
			publicationActionSuccess,
			refreshAfterError,
			(state) => state.setActionInProgress('none')
		),
		on(refreshDatablocksSuccess, (state, action) =>
			state.setDatablockList(action.datablocks)
		),
		on(publicationActionSuccess, (state, action) =>
			state.setInViewExposition(
				state.inViewExposition.setPublishedApi(
					state.inViewExposition.published.updateStatus(action.status)
				)
			)
		),
		on(refreshExpositionAfterWS, (state, action) =>
			state.setExpositionList(
				state.expositions.resetRunningActionOn(action.expositionId)
			)
		),
		on(fetchAvailableExpositionsForMigrationSuccess, (state, action) =>
			state.setV1Expositions(action.eligible)
		),
		on(retrievesConsumersToMapSuccess, (state, action) =>
			state
				.setRequestId(action.requestId)
				.setUnmappedConsumers(action.unmappedConsumers)
				.setAvailableConsumers(action.availableConsumers)
		),
		on(autoMappingSuccess, (state, action) =>
			state.setUnmappedConsumers(action.consumersToMap)
		),
		on(
			closeMigrateExpositionModal,
			migrateSelectedExpositionsSuccess,
			(state) => state.resetConsumersToMap()
		),
		on(projectImportConsumersToMapSuccess, (state, action) =>
			state
				.setRequestId('')
				.setUnmappedConsumers([])
				.setAvailableConsumers(action.availableConsumers)
		),
		on(fetchMigratedExpositionsListSuccess, (state, action) =>
			state.setMigrated(action.migrated)
		),
		on(fetchExpositionHistorySuccess, (state, action) =>
			state.setHistory(action.history)
		),
		on(
			fetchExpositionOnHistoryNavigationSuccess,
			refreshAfterErrorSuccess,
			(state, action) => state.setInViewExposition(action.exposition)
		),
		on(getJobErrorMessage, (state) => state.setIsLoadingErrorLogs(true)),
		on(fetchJobErrorMessageFail, (state) => state.setIsLoadingErrorLogs(false)),
		on(fetchJobErrorMessageSuccess, (state, action) =>
			state
				.setHistory(
					state.history.updateJobLog(action.id.toString(), action.message)
				)
				.setIsLoadingErrorLogs(false)
		),
		on(fetchExpositionViewOnEndpointDetailsTriggerSuccess, (state, action) =>
			state
				.setInViewExposition(action.exposition)
				.toggleLoadingMetadataInfo(false)
		),
		on(fetchExpositionViewOnEndpointDetailsTrigger, (state) =>
			state.toggleLoadingMetadataInfo(true)
		),
		on(fetchEligibleConsumersSuccess, (state, action) =>
			state.setEligibleConsumers(action.consumers, action.total)
		),
		on(resetEligibleConsumers, (state) => state.resetEligibleConsumers())
	);
